import { z } from 'zod';

import { RegisterPaymentLineBodySchema } from '../finance/finance.schemas.js';

const CentsSchema = z.string().regex(/^\d+$/, 'must be a non-negative integer string (cents)');

export const CaixaOutboundDocumentKindSchema = z.enum(['NFE', 'NFCE', 'NFSE']);
export type CaixaOutboundDocumentKind = z.infer<typeof CaixaOutboundDocumentKindSchema>;

export const CaixaKindResolveReasonSchema = z.enum([
  'TAXPAYER_REQUIRES_NFE',
  'END_CONSUMER_NFCE',
  'NFCE_AMOUNT_EXCEEDED',
  'SERVICE_NFSE',
]);
export type CaixaKindResolveReason = z.infer<typeof CaixaKindResolveReasonSchema>;

export const CaixaCustomerGapCodeSchema = z.enum([
  'MISSING_STATE_REGISTRATION',
  'MISSING_DESTINATION_ADDRESS',
  'MISSING_CUSTOMER_DOCUMENT',
]);
export type CaixaCustomerGapCode = z.infer<typeof CaixaCustomerGapCodeSchema>;

export const CaixaOperationGapCodeSchema = z.enum([
  'MISSING_NFE_OPERATION',
  'MISSING_NFCE_OPERATION',
  'MISSING_NFSE_OPERATION',
]);
export type CaixaOperationGapCode = z.infer<typeof CaixaOperationGapCodeSchema>;

export const CaixaPreflightParamsSchema = z.looseObject({
  placeId: z.uuid(),
  serviceOrderId: z.uuid(),
});

export const CaixaDocumentPreviewSchema = z.looseObject({
  documentKind: CaixaOutboundDocumentKindSchema,
  amountCents: CentsSchema.nullable(),
  placeFiscalOperationId: z.uuid().nullable(),
  placeFiscalOperationLabel: z.string().nullable(),
});

export const CaixaPreflightResponseSchema = z.looseObject({
  requiredKinds: z.array(CaixaOutboundDocumentKindSchema),
  productKind: z.enum(['NFE', 'NFCE']).nullable(),
  upgradedFromNfce: z.boolean(),
  reasons: z.array(CaixaKindResolveReasonSchema),
  customerGaps: z.array(CaixaCustomerGapCodeSchema),
  operationGaps: z.array(CaixaOperationGapCodeSchema),
  otherGaps: z
    .array(z.enum(['UNRESOLVED_BILLABLE_ITEMS', 'EXISTING_PENDING_OBLIGATION']))
    .optional()
    .default([]),
  canSettle: z.boolean(),
  blockCaixaOnFiscalPending: z.boolean(),
  nfceMaxAmountCents: CentsSchema,
  documentsPreview: z.array(CaixaDocumentPreviewSchema),
  customerPartyId: z.uuid().nullable(),
});
export type CaixaPreflightResponse = z.infer<typeof CaixaPreflightResponseSchema>;

export const SettleCaixaFromServiceOrderParamsSchema = CaixaPreflightParamsSchema;

export const SettleCaixaFromServiceOrderBodySchema = z.looseObject({
  discountCents: CentsSchema.optional().default('0'),
  lines: z.array(RegisterPaymentLineBodySchema).min(1),
});
export type SettleCaixaFromServiceOrderBody = z.infer<typeof SettleCaixaFromServiceOrderBodySchema>;

export const SettledCaixaDocumentSchema = z.looseObject({
  documentKind: CaixaOutboundDocumentKindSchema,
  fiscalDocumentId: z.uuid(),
  status: z.string().min(1),
  alreadyExisted: z.boolean().optional(),
});

export const SettledCaixaPendingObligationSchema = z.looseObject({
  id: z.uuid(),
  documentKind: CaixaOutboundDocumentKindSchema,
  status: z.enum(['PENDING', 'RESOLVED', 'FAILED']),
  lastErrorCode: z.string().nullable().optional(),
});

export const SettleCaixaFromServiceOrderResponseSchema = z.looseObject({
  settlementGroupId: z.uuid(),
  paymentId: z.uuid().optional(),
  documents: z.array(SettledCaixaDocumentSchema),
  pendingObligations: z.array(SettledCaixaPendingObligationSchema).optional().default([]),
});
export type SettleCaixaFromServiceOrderResponse = z.infer<
  typeof SettleCaixaFromServiceOrderResponseSchema
>;

export const PlaceFiscalCaixaPolicyParamsSchema = z.looseObject({
  placeId: z.uuid(),
});

export const PlaceFiscalCaixaPolicyResponseSchema = z.looseObject({
  blockCaixaOnFiscalPending: z.boolean(),
  nfceMaxAmountCents: CentsSchema,
});
export type PlaceFiscalCaixaPolicyResponse = z.infer<typeof PlaceFiscalCaixaPolicyResponseSchema>;

export const UpsertPlaceFiscalCaixaPolicyBodySchema = z.looseObject({
  blockCaixaOnFiscalPending: z.boolean(),
  nfceMaxAmountCents: CentsSchema.optional(),
});
export type UpsertPlaceFiscalCaixaPolicyBody = z.infer<
  typeof UpsertPlaceFiscalCaixaPolicyBodySchema
>;

export const ListFiscalPendingObligationsParamsSchema = PlaceFiscalCaixaPolicyParamsSchema;

export const ListFiscalPendingObligationsQuerySchema = z.looseObject({
  status: z.enum(['PENDING', 'RESOLVED', 'FAILED']).optional().default('PENDING'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().max(100).optional().default(20),
});
export type ListFiscalPendingObligationsQuery = z.infer<
  typeof ListFiscalPendingObligationsQuerySchema
>;

export const FiscalPendingObligationListItemSchema = z.looseObject({
  id: z.uuid(),
  serviceOrderId: z.uuid(),
  serviceOrderDisplayNumber: z.string().nullable().optional(),
  documentKind: CaixaOutboundDocumentKindSchema,
  status: z.enum(['PENDING', 'RESOLVED', 'FAILED']),
  lastErrorCode: z.string().nullable().optional(),
  attemptCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});

export const ListFiscalPendingObligationsResponseSchema = z.looseObject({
  items: z.array(FiscalPendingObligationListItemSchema),
  nextCursor: z.string().nullable(),
});
export type ListFiscalPendingObligationsResponse = z.infer<
  typeof ListFiscalPendingObligationsResponseSchema
>;

export const RetryFiscalPendingObligationParamsSchema = z.looseObject({
  placeId: z.uuid(),
  obligationId: z.uuid(),
});

export const RetryFiscalPendingObligationResponseSchema = z.looseObject({
  id: z.uuid(),
  status: z.enum(['PENDING', 'RESOLVED', 'FAILED']),
  fiscalDocumentId: z.uuid().nullable(),
  lastErrorCode: z.string().nullable().optional(),
});
export type RetryFiscalPendingObligationResponse = z.infer<
  typeof RetryFiscalPendingObligationResponseSchema
>;
